<template>
<div class="ew-landing">
  <navbar-public></navbar-public>

  <!-- Hero: el widget de cotización ES el hero. Es lo que la gente viene a
       hacer y lo que ninguna plataforma competidora ofrece sin registro. -->
  <header class="ew-hero">
    <div class="ew-hero__inner">
      <div class="ew-hero__pitch">
        <h1 class="ew-hero__title">Encuentra el mejor precio de envío en una sola búsqueda</h1>
        <p class="ew-hero__subtitle">
          Compara al mismo tiempo las tarifas de las principales paqueterías del país.
          Elige la que más te convenga, genera tu guía en minutos y paga solo los envíos que hagas.
        </p>

        <ul class="ew-hero__points">
          <li><ph-icon name="check" /><span>Cotiza sin crear cuenta</span></li>
          <li><ph-icon name="check" /><span>Sin mensualidad ni volumen mínimo</span></li>
          <li><ph-icon name="check" /><span>Guía lista para imprimir al instante</span></li>
        </ul>

        <!-- Visual decorativo: ilustra el mecanismo del producto (comparar
             varias paqueterías en una sola búsqueda), no es una cotización
             real ni tarifas vigentes. `aria-hidden` porque no aporta
             información que no esté ya en el texto o en el widget. -->
        <div class="ew-hero__compare" aria-hidden="true">
          <div class="ew-hero__route">
            <span class="ew-hero__pin">
              <span class="ew-hero__pin-icon"><ph-icon name="map-pin" /></span>
              <b>CDMX</b>
            </span>
            <span class="ew-hero__route-line">
              <span class="ew-hero__route-package"><ph-icon name="package" /></span>
            </span>
            <span class="ew-hero__pin">
              <span class="ew-hero__pin-icon"><ph-icon name="map-pin" /></span>
              <b>Monterrey</b>
            </span>
          </div>
          <div class="ew-hero__quotes">
            <span class="ew-hero__quote"><span>FedEx</span><b>$246</b></span>
            <span class="ew-hero__quote"><span>DHL</span><b>$219</b></span>
            <span class="ew-hero__quote ew-hero__quote--best"><span>Estafeta</span><b>$185</b></span>
          </div>
          <p class="ew-hero__compare-caption">Ejemplo ilustrativo de comparación de precios</p>
        </div>
      </div>

      <!-- Widget de cotización: misma lógica de siempre (checkAddressOrigin,
           setResult, calculateQuote...), solo cambia la presentación. -->
      <section class="ew-quote" id="ew-container-quoting">
        <a name="cotizar"></a>
        <h2 class="ew-quote__title">Cotiza tu envío</h2>

        <div class="ew-quote__row">
          <div class="ew-quote__field">
            <label class="ew-quote__label" for="ew-origin">Origen</label>
            <div class="ew-quote__input-group">
              <span class="ew-quote__country">México</span>
              <input
                id="ew-origin"
                type="text"
                class="ew-quote__input"
                placeholder="C.P. de origen"
                @keyup="checkAddressOrigin"
                @keyup.down="onArrowDown"
                @keyup.up="onArrowUp"
                @keyup.enter="onEnter"
                v-model.trim="origin.input"
                @click="closeQuoting()"
                autocomplete="off"
                required
              >
            </div>
            <div v-if="isLoading" v-show="isOpen" class="ew-quote__suggestions-wrap">
              <ul class="autocomplete-results" id="autocomplete-results">
                <li
                  id="resultOrigin"
                  v-for="(result, i) in results"
                  :key="i"
                  @click="setResult(result)"
                  class="autocomplete-result"
                  :class="{ 'is-active': i === arrowCounter }"
                >{{ result.postal_code + ' - ' + result.place_name + ' - ' + result.city }}</li>
              </ul>
            </div>
            <p class="ew-quote__error" v-if="originIvalid">Elige un código postal de la lista de sugerencias.</p>
          </div>

          <div class="ew-quote__field">
            <label class="ew-quote__label" for="ew-destination">Destino</label>
            <div class="ew-quote__input-group">
              <b-dropdown
                v-if="countries.length"
                class="ew-quote__country-dd"
                variant="link"
                no-caret
              >
                <template slot="button-content">{{ destination.country_code }}</template>
                <b-dropdown-item
                  v-for="c in countries"
                  @click="setDestination(c)"
                  v-bind:key="c.name"
                >{{ c.name }}</b-dropdown-item>
              </b-dropdown>
              <span class="ew-quote__country" v-else>México</span>
              <input
                id="ew-destination"
                type="text"
                class="ew-quote__input"
                placeholder="C.P. de destino"
                @keyup="checkAddressDestination"
                @keyup.down="onArrowDownDes"
                @keyup.up="onArrowUpDes"
                @keyup.enter="onEnterDes"
                v-model.trim="destination.input"
                @click="closeQuoting()"
                autocomplete="off"
                required
              >
            </div>
            <div v-if="isLoadingDes" v-show="isOpenDes" class="ew-quote__suggestions-wrap">
              <ul class="autocomplete-results" id="autocomplete-results-des">
                <li
                  id="resultDestination"
                  v-for="(result, i) in resultsDes"
                  :key="i"
                  @click="setResultDes(result)"
                  class="autocomplete-result"
                  :class="{ 'is-active': i === arrowCounterDes }"
                >{{ result.postal_code + ' - ' + result.place_name + ' - ' + result.city }}</li>
              </ul>
            </div>
            <p class="ew-quote__error" v-if="destinationInvalid">Elige un código postal de la lista de sugerencias.</p>
          </div>
        </div>

        <p class="ew-quote__label ew-quote__label--block">¿Qué vas a enviar?</p>
        <div class="ew-quote__types">
          <label class="ew-pill" :class="{ 'is-active': package_type === 'document' }">
            <input type="radio" name="ew-package-type" @click="setDocument">
            <span>Sobre</span>
          </label>
          <label class="ew-pill" :class="{ 'is-active': package_type === 'box' }">
            <input type="radio" name="ew-package-type" @click="setBox">
            <span>Paquete</span>
          </label>
          <label class="ew-pill" :class="{ 'is-active': package_type === 'tarima' }">
            <input type="radio" name="ew-package-type" @click="setPallet">
            <span>Tarima</span>
          </label>
        </div>

        <div class="ew-quote__parcels" v-for="(parcel, index) in parcels" v-bind:key="index">
          <div class="ew-quote__field" v-if="package_type !== 'document'">
            <label class="ew-quote__label" :for="'ew-largo-' + index">Largo</label>
            <input
              :id="'ew-largo-' + index"
              type="number"
              class="ew-quote__input ew-quote__input--solo"
              placeholder="cm"
              @keyup="numberIntegerPositiveWidth"
              v-model.trim="parcels[index].width"
              min="1"
            >
          </div>
          <div class="ew-quote__field" v-if="package_type !== 'document'">
            <label class="ew-quote__label" :for="'ew-alto-' + index">Alto</label>
            <input
              :id="'ew-alto-' + index"
              type="number"
              class="ew-quote__input ew-quote__input--solo"
              placeholder="cm"
              @keyup="numberIntegerPositiveHeight"
              v-model.trim="parcels[index].height"
              min="1"
            >
          </div>
          <div class="ew-quote__field" v-if="package_type !== 'document'">
            <label class="ew-quote__label" :for="'ew-ancho-' + index">Ancho</label>
            <input
              :id="'ew-ancho-' + index"
              type="number"
              class="ew-quote__input ew-quote__input--solo"
              placeholder="cm"
              @keyup="numberIntegerPositiveDepth"
              v-model.trim="parcels[index].depth"
              min="1"
              step="1"
            >
          </div>
          <div class="ew-quote__field">
            <label class="ew-quote__label" :for="'ew-peso-' + index">Peso</label>
            <input
              :id="'ew-peso-' + index"
              type="number"
              class="ew-quote__input ew-quote__input--solo"
              placeholder="kg"
              v-model.trim="parcels[index].weight"
              min="0.1"
              step="0.1"
              @keyup="numberFloatPositiveWeight"
            >
          </div>
        </div>

        <label class="ew-quote__insurance" v-if="package_type === 'document'">
          <input type="checkbox" v-model="insureDocument">
          <span>Quiero asegurar mi envío</span>
        </label>
        <label class="ew-quote__insurance" v-else>
          <input type="checkbox" v-model="insurePackage">
          <span>Quiero asegurar mi envío</span>
        </label>

        <button type="submit" class="ew-btn-primary ew-quote__submit" @click="calculateQuote">Ver precios</button>

        <p class="ew-quote__hint">
          Usa el peso y las medidas reales: si no coinciden, la paquetería cobra la diferencia después.
        </p>
      </section>
    </div>
  </header>

  <!-- Prueba: con quién cotizas realmente. La lista viene de `carriers`, que
       es exactamente lo que el backend sabe cotizar hoy. El segundo grupo es
       una copia para que el desplazamiento cierre sin salto. -->
  <section class="ew-carriers">
    <div class="ew-carriers__viewport">
      <div class="ew-carriers__track">
        <ul class="ew-carriers__group">
          <li v-for="c in carriers" :key="c.name">
            <img :src="c.logo" :alt="c.name">
          </li>
        </ul>
        <ul class="ew-carriers__group" aria-hidden="true">
          <li v-for="c in carriers" :key="'dup-' + c.name">
            <img :src="c.logo" :alt="c.name">
          </li>
        </ul>
      </div>
    </div>
  </section>

  <!-- Cómo funciona: es una secuencia real, por eso va numerado -->
  <section class="ew-section-block">
    <div class="ew-container">
      <h2 class="ew-h2">De cotizar a enviar, en tres pasos</h2>
      <ol class="ew-steps">
        <li class="ew-step">
          <span class="ew-step__num">1</span>
          <h3 class="ew-step__title">Cotiza</h3>
          <p class="ew-step__text">
            Captura el código postal de origen y destino, el peso y las medidas.
            Ve cada paquetería con su precio y su tiempo de entrega.
          </p>
        </li>
        <li class="ew-step">
          <span class="ew-step__num">2</span>
          <h3 class="ew-step__title">Elige y paga</h3>
          <p class="ew-step__text">
            Quédate con la opción que más te convenga. Paga con tarjeta, PayPal,
            en efectivo en OXXO o con el saldo de tu monedero.
          </p>
        </li>
        <li class="ew-step">
          <span class="ew-step__num">3</span>
          <h3 class="ew-step__title">Imprime y envía</h3>
          <p class="ew-step__text">
            Recibes la guía en tu correo, lista para imprimir y pegar en el paquete.
            Después sigues el envío desde tu panel.
          </p>
        </li>
      </ol>
    </div>
  </section>

  <!-- Banda de contraste. Las dos primeras cifras son de negocio; la de
       ciudades sale del catálogo de códigos postales (2,322 municipios en
       la colección `locations`). Los números cuentan hacia arriba la
       primera vez que la banda entra en pantalla. -->
  <section class="ew-band" data-reveal>
    <div class="ew-container ew-band__grid">
      <div class="ew-band__item" v-for="(s, i) in statsTop" :key="'top-' + i">
        <p class="ew-band__value">{{ statValue(s) }}</p>
        <p class="ew-band__label">{{ s.label }}</p>
      </div>
    </div>
  </section>

  <!-- Beneficios + prueba real del producto: hasta ahora la página solo
       prometía "rastreo en un panel" y "guía lista para imprimir" en texto;
       esta captura (cuenta y envíos de muestra, no de un cliente real) lo
       muestra. Sin sombra en la lista para que la única pieza elevada de la
       página siga siendo el widget del hero. -->
  <section class="ew-section-block">
    <div class="ew-container ew-showcase">
      <div class="ew-showcase__text">
        <h2 class="ew-h2">Por qué enviar con Moovet</h2>
        <div class="ew-benefits">
          <div class="ew-benefit">
            <span class="ew-benefit__icon"><ph-icon name="scales" /></span>
            <div>
              <h3 class="ew-benefit__title">Compara antes de pagar</h3>
              <p class="ew-benefit__text">Ves el precio y el tiempo de entrega de cada paquetería lado a lado, en la misma pantalla, antes de decidir.</p>
            </div>
          </div>
          <div class="ew-benefit">
            <span class="ew-benefit__icon"><ph-icon name="wallet" /></span>
            <div>
              <h3 class="ew-benefit__title">Monedero para envíos diarios</h3>
              <p class="ew-benefit__text">Carga saldo una vez y genera guías sin volver a capturar datos de pago en cada envío.</p>
            </div>
          </div>
          <div class="ew-benefit">
            <span class="ew-benefit__icon"><ph-icon name="map-pin" /></span>
            <div>
              <h3 class="ew-benefit__title">Rastreo en un solo panel</h3>
              <p class="ew-benefit__text">Sigue todos tus envíos desde la misma pantalla, sin entrar al sitio de cada paquetería.</p>
            </div>
          </div>
          <div class="ew-benefit">
            <span class="ew-benefit__icon"><ph-icon name="puzzle-piece" /></span>
            <div>
              <h3 class="ew-benefit__title">Conecta tu tienda</h3>
              <p class="ew-benefit__text">Sincroniza los pedidos de tu tienda en línea y genera las guías desde aquí, sin capturar direcciones a mano.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Simulación del recorrido real: cotizar, elegir paquetería y
           terminar con la guía. Los dos primeros pasos son una recreación
           de la interfaz (datos de ejemplo); el tercero es una captura
           real del panel. Avanza solo mientras está en pantalla. -->
      <div class="ew-showcase__visual" data-demo>
        <div class="ew-browser-frame">
          <div class="ew-browser-frame__bar">
            <span class="ew-browser-frame__dot"></span>
            <span class="ew-browser-frame__dot"></span>
            <span class="ew-browser-frame__dot"></span>
            <span class="ew-browser-frame__url">{{ demoUrl }}</span>
          </div>

          <div class="ew-demo" :class="'is-step-' + demoStep" aria-hidden="true">
            <!-- Paso 1: cotizar -->
            <div class="ew-demo__pane ew-demo__pane--form">
              <p class="ew-demo__title">Cotiza tu envío</p>
              <div class="ew-demo__fields">
                <div class="ew-demo__field">
                  <span class="ew-demo__label">Origen</span>
                  <span class="ew-demo__input">76910 · Querétaro</span>
                </div>
                <div class="ew-demo__field">
                  <span class="ew-demo__label">Destino</span>
                  <span class="ew-demo__input">64000 · Monterrey</span>
                </div>
                <div class="ew-demo__field ew-demo__field--sm">
                  <span class="ew-demo__label">Peso</span>
                  <span class="ew-demo__input">2 kg</span>
                </div>
                <div class="ew-demo__field ew-demo__field--sm">
                  <span class="ew-demo__label">Medidas</span>
                  <span class="ew-demo__input">30 × 20 × 15</span>
                </div>
              </div>
              <span class="ew-demo__btn">Ver precios</span>
            </div>

            <!-- Paso 2: elegir paquetería -->
            <div class="ew-demo__pane ew-demo__pane--rates">
              <p class="ew-demo__title">Elige tu paquetería</p>
              <div class="ew-demo__rates">
                <div
                  class="ew-demo__rate"
                  v-for="(r, i) in demoRates"
                  :key="r.name"
                  :class="{ 'is-best': r.best }"
                >
                  <img :src="r.logo" :alt="r.name">
                  <span class="ew-demo__rate-days">{{ r.days }}</span>
                  <span class="ew-demo__rate-price">${{ r.price }}</span>
                  <span class="ew-demo__rate-tag" v-if="r.best">Más barato</span>
                </div>
              </div>
            </div>

            <!-- Paso 3: la guía, ya en el panel real -->
            <div class="ew-demo__pane ew-demo__pane--done">
              <img
                src="@/assets/images/dashboard-preview.png"
                alt=""
                class="ew-demo__shot"
              >
              <span class="ew-demo__toast"><ph-icon name="check" />Guía generada</span>
            </div>
          </div>
        </div>

        <ol class="ew-demo-steps">
          <li
            v-for="(s, i) in demoSteps"
            :key="s"
            :class="{ 'is-active': demoStep === i + 1, 'is-done': demoStep > i + 1 }"
          >{{ s }}</li>
        </ol>
      </div>
    </div>
  </section>

  <!-- Segunda banda de cifras: cobertura internacional y condiciones del
       servicio, después de los beneficios en vez de todas juntas al inicio. -->
  <section class="ew-band" data-reveal>
    <div class="ew-container ew-band__grid">
      <div class="ew-band__item" v-for="(s, i) in statsBottom" :key="'bottom-' + i">
        <p class="ew-band__value">{{ statValue(s) }}</p>
        <p class="ew-band__label">{{ s.label }}</p>
      </div>
    </div>
  </section>

  <!-- Integraciones: cuatro vías de conexión, cada una entra escalonada
       cuando la sección aparece en pantalla. -->
  <section class="ew-section-block ew-section-block--muted">
    <div class="ew-container ew-integrations" data-reveal>
      <div class="ew-integrations__text">
        <h2 class="ew-h2">Ya vendes en línea. Conéctalo.</h2>
        <p class="ew-lead">
          Enlaza tu tienda y tus pedidos llegan solos a Moovet, con la dirección
          del comprador ya capturada. Tú eliges la paquetería y generas la guía.
        </p>
        <router-link class="ew-btn-primary" to="/signup">Crear cuenta gratis</router-link>
      </div>

      <div class="ew-channels">
        <div class="ew-channel">
          <span class="ew-channel__mark">
            <img src="@/assets/images/shopify.svg" alt="Shopify">
          </span>
          <div class="ew-channel__body">
            <p class="ew-channel__name">Shopify</p>
            <p class="ew-channel__text">Tus pedidos entran solos, con la dirección del comprador ya capturada.</p>
          </div>
        </div>

        <div class="ew-channel">
          <span class="ew-channel__mark">
            <img src="@/assets/images/logo-woocommerce.png" alt="WooCommerce">
          </span>
          <div class="ew-channel__body">
            <p class="ew-channel__name">WooCommerce</p>
            <p class="ew-channel__text">Mismo flujo para tu tienda en WordPress, sin capturar nada a mano.</p>
          </div>
        </div>

        <div class="ew-channel">
          <span class="ew-channel__mark ew-channel__mark--icon"><ph-icon name="code" /></span>
          <div class="ew-channel__body">
            <p class="ew-channel__name">API de Moovet</p>
            <p class="ew-channel__text">Cotiza y genera guías desde tu propio sistema o ERP.</p>
          </div>
        </div>

        <div class="ew-channel ew-channel--soon">
          <span class="ew-channel__mark ew-channel__mark--icon"><ph-icon name="sparkle" /></span>
          <div class="ew-channel__body">
            <p class="ew-channel__name">
              Integración con IA
              <span class="ew-channel__badge">Próximamente</span>
            </p>
            <p class="ew-channel__text">Automatiza la elección de paquetería y la captura de tus envíos.</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Preguntas frecuentes: las mismas que ya están en /preguntas-frecuentes -->
  <section class="ew-section-block">
    <div class="ew-container">
      <h2 class="ew-h2">Preguntas frecuentes</h2>
      <div class="ew-faq">
        <div class="ew-faq__item" v-for="(q, i) in faq" :key="i">
          <p class="ew-faq__q">{{ q.question }}</p>
          <p class="ew-faq__a" v-html="q.answer"></p>
        </div>
      </div>
      <router-link class="ew-link-btn" to="/preguntas-frecuentes">Ver todas las preguntas</router-link>
    </div>
  </section>

  <!-- Cierre -->
  <section class="ew-final">
    <div class="ew-container ew-final__inner">
      <h2 class="ew-final__title">Haz tu primer envío hoy</h2>
      <p class="ew-final__text">Crear la cuenta es gratis y solo pagas los envíos que generes.</p>
      <div class="ew-final__actions">
        <router-link class="ew-btn-primary" to="/signup">Crear cuenta gratis</router-link>
        <button type="button" class="ew-btn-ghost" @click="scrollToBottom">Cotizar un envío</button>
      </div>
    </div>
  </section>

  <footer-public></footer-public>
</div>
</template>

<script>

import axios from "axios";
import { mapGetters, mapActions, mapMutations } from "vuex";
import service from '../commons/api'
import { WOW } from "wowjs";
import commons from "../commons";
import Navbar from "./Navbar";
import NavbarPublic from "./Header/NavbarPublic";
import FooterPublic from "./Header/FooterPublic";
import PhIcon from "./icons/PhIcon";

export default {
  name: "Home",
  data() {
    return {
      slide: 0,
      sliding: null,
      /* Cifras de las dos bandas. `target` es el número al que cuenta la
         animación y `current` el valor mostrado mientras cuenta; las que
         traen `text` no son numéricas y solo aparecen con fundido. */
      statsTop: [
        { prefix: "+", target: 50, suffix: "M", current: 0, label: "guías creadas con Moovet" },
        { prefix: "+", target: 3, suffix: "K", current: 0, label: "clientes que ya envían con Moovet" },
        { prefix: "+", target: 2300, suffix: "", current: 0, label: "ciudades con cobertura en México" }
      ],
      statsBottom: [
        { prefix: "+", target: 220, suffix: "", current: 0, label: "países a los que puedes enviar" },
        { text: "$0", label: "de mensualidad, sin contrato, mejor precio por volumen" },
        { text: "24/7", label: "para cotizar y generar guías a cualquier hora" }
      ],
      revealObserver: null,
      demoObserver: null,
      demoTimer: null,
      demoStep: 1,
      demoSteps: ["Cotiza", "Elige", "Genera la guía"],
      /* Precios de ejemplo para la simulación, no tarifas vigentes. */
      demoRates: [
        { name: "FedEx", days: "2 días", price: "246", logo: require("@/assets/images/fedex.svg") },
        { name: "DHL", days: "2 días", price: "219", logo: require("@/assets/images/dhl.png") },
        { name: "Estafeta", days: "3 días", price: "185", best: true, logo: require("@/assets/images/Estafeta.png") }
      ],
      faq: [
        {
          question: "\u00bfPor qu\u00e9 cotizar aqu\u00ed y no directamente con la paqueter\u00eda?",
          answer: "Por volumen accedes a tarifas m\u00e1s bajas que las de mostrador, y en una sola b\u00fasqueda ves el precio de todas las paqueter\u00edas en vez de entrar al sitio de cada una."
        },
        {
          question: "\u00bfQu\u00e9 determina el costo de mi env\u00edo?",
          answer: "El peso en kilos y el peso volum\u00e9trico; se cobra el que resulte mayor. <strong>Peso volum\u00e9trico = largo \u00d7 ancho \u00d7 alto \u00f7 5000.</strong>"
        },
        {
          question: "\u00bfQu\u00e9 pasa si mi paquete pesa m\u00e1s de lo que declar\u00e9?",
          answer: "La paqueter\u00eda cobra la diferencia como sobrepeso. Te llega la notificaci\u00f3n por correo y el paquete se retiene hasta que se salde."
        },
        {
          question: "\u00bfQui\u00e9n recoge y entrega el paquete?",
          answer: "La paqueter\u00eda que elijas: recolecta y entrega en las direcciones que captures en la gu\u00eda."
        }
      ],
      /* Solo las paqueterías que el backend cotiza de verdad: DHL, FedEx y
         AMPM por integración directa, y Paquetexpress, Estafeta y Tresguerras
         vía Epak (shippingService/epak/quoteResponse.js). */
      carriers: [
        { name: "DHL", logo: require("@/assets/images/dhl.png") },
        { name: "FedEx", logo: require("@/assets/images/fedex.svg") },
        { name: "Estafeta", logo: require("@/assets/images/Estafeta.png") },
        { name: "Paquetexpress", logo: require("@/assets/images/paquetexpress.svg") },
        { name: "ampm", logo: require("@/assets/images/ampm.svg") },
        { name: "Tresguerras", logo: require("@/assets/images/tresguerras.svg") }
      ],
      originIvalid: false,
      destinationInvalid: false,
      isBox: false,
      isPallet: false,
      isDocument: false,
      div: null,
      isOpen: false,
      results: [],
      scroll: 0,
      isLoading: false,
      arrowCounter: -1,

      isOpenDes: false,
      resultsDes: [],
      scrollDes: 0,
      isLoadingDes: false,
      arrowCounterDes: -1,

      countries: [],
      origin: {
        country_code: "MX",
        flag: "https://restcountries.eu/data/mex.svg",
        input: ""
      },
      destination: {
        country_code: "MX",
        flag: "https://restcountries.eu/data/mex.svg",
        input: ""
      },
      package_type: "box",
      insurePackage: false,
      insureDocument: false,
      parcels: [
        {
          depth: "",
          width: "",
          height: "",
          weight: ""
        }
      ],
      requestBody: {
        origin_direction: {
          country_code: "",
          postal_code: "",
          city: "",
          suburb: ""
        },

        parcels: [
          {
            weight: "",
            height: "",
            depth: "",
            width: ""
          }
        ],
        destination_direction: {
          country_code: "",
          postal_code: "",
          city: "",
          suburb: "",
          state_short:""
        },
        insured_value: "",
        insured_value_currency: "MXN"
      },
      originSuggestions: [],
      destinationSuggestions: [],

      quotingLocked: true,
      s1: null,
      s0: null
    };
  },
  methods: {
    /* Texto que se muestra en cada cifra: las numéricas van formateadas
       con separador de miles mientras cuentan; las de texto fijo ("$0",
       "24/7") se devuelven tal cual. */
    statValue(stat) {
      if (stat.text) return stat.text;
      return stat.prefix + stat.current.toLocaleString("es-MX") + stat.suffix;
    },
    /* Un solo observer para todo lo que lleva `data-reveal`: agrega la
       clase que dispara la animación de CSS y, si el bloque es una banda
       de cifras, arranca el conteo. Se desobserva al primer disparo para
       que no se repita en cada scroll. */
    setupReveal() {
      const reduce =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const targets = this.$el.querySelectorAll("[data-reveal]");

      if (reduce || typeof IntersectionObserver === "undefined") {
        targets.forEach((el) => el.classList.add("is-revealed"));
        this.statsTop.forEach((s) => { s.current = s.target; });
        this.statsBottom.forEach((s) => { if (s.target) s.current = s.target; });
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            if (entry.target.classList.contains("ew-band")) {
              const list =
                entry.target === this.$el.querySelector(".ew-band")
                  ? this.statsTop
                  : this.statsBottom;
              list.forEach((s, i) => {
                if (s.target) this.countUp(s, 1100, i * 120);
              });
            }
            this.revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.25 }
      );

      targets.forEach((el) => this.revealObserver.observe(el));
      this.setupDemo(reduce);
    },
    /* La simulación solo corre mientras se ve: fuera de pantalla no tiene
       sentido gastar ciclos, y al volver retoma desde el primer paso. */
    setupDemo(reduce) {
      const el = this.$el.querySelector("[data-demo]");
      if (!el) return;

      if (reduce || typeof IntersectionObserver === "undefined") {
        this.demoStep = 3;
        return;
      }

      this.demoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.startDemo();
            else this.stopDemo();
          });
        },
        { threshold: 0.35 }
      );
      this.demoObserver.observe(el);
    },
    startDemo() {
      if (this.demoTimer) return;
      this.demoStep = 1;
      this.demoTimer = setInterval(() => {
        this.demoStep = this.demoStep >= 3 ? 1 : this.demoStep + 1;
      }, 2600);
    },
    stopDemo() {
      if (!this.demoTimer) return;
      clearInterval(this.demoTimer);
      this.demoTimer = null;
    },
    countUp(stat, duration, delay) {
      const start = () => {
        const t0 = performance.now();
        const step = (now) => {
          const p = Math.min((now - t0) / duration, 1);
          /* easeOutCubic: arranca rápido y frena al final, se lee mejor
             que un conteo lineal. */
          const eased = 1 - Math.pow(1 - p, 3);
          stat.current = Math.round(stat.target * eased);
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };
      if (delay) setTimeout(start, delay);
      else start();
    },

    scrollToBottom() {
     document.getElementById("ew-container-quoting").scrollIntoView({
      behavior: "smooth"
    });
   },
    numberIntegerPositiveWidth(event){
      let index = 0
      if (Number(event.target.value) > 0) {
        this.parcels[index].width = Math.ceil(Number(event.target.value)).toString()
      }
    },
    numberIntegerPositiveHeight(event){
      let index = 0
      if (Number(event.target.value) > 0) {
        this.parcels[index].height = Math.ceil(Number(event.target.value)).toString()
      }
    },
    numberIntegerPositiveDepth(event){
      let index = 0
      if (Number(event.target.value) > 0) {
        this.parcels[index].depth = Math.ceil(Number(event.target.value)).toString()
      }
    },
    numberFloatPositiveWeight(event){
      let index = 0
       if (Number(event.target.value) > 0) {
        this.parcels[index].weight = Number(event.target.value).toString();
       }

    },
    formatAddress(countryCode, rawAddress) {
      let address = {
        country_code: countryCode
      };

      if (rawAddress.includes(" - ")) {
        const addressList = rawAddress.split(" - ");

        address["postal_code"] = addressList[0];

        if (addressList.length === 3) {
          address["suburb"] = addressList[1];
          address["city"] = addressList[2];
        } else {
          address["city"] = addressList[1];
        }

        return address;
      }

      address["postal_code"] = rawAddress;

      return address;
    },
    calculateQuote() {
      let validateOrigin = this.validateAddress(this.origin.input);
      let validateDestination = this.validateAddress(this.destination.input);
      if (validateOrigin && validateDestination) {
        const parcelsValid = () => {
          if (!this.parcels.length) {
            return false;
          }

          return this.parcels.every(parcel => {
            if (this.package_type === "document") {
              return parcel.weight > 0;
            }

            return (
              parcel.height > 0 &&
              parcel.width > 0 &&
              parcel.depth > 0 &&
              parcel.weight > 0
            );
          });
        };

        if (
          !this.origin.input.length ||
          !this.destination.input.length ||
          !parcelsValid()
        ) {
          return;
        }

        this.requestBody.origin_direction = this.formatAddress(
          this.origin.country_code,
          this.origin.input
        );
        let state_short = this.requestBody.destination_direction.state_short;
        this.requestBody.destination_direction = this.formatAddress(
          this.destination.country_code,
          this.destination.input
        );

        this.requestBody.destination_direction["state_short"] = state_short;
       console.log("this.requestBody.destination_direction.state_short", this.requestBody.destination_direction.state_short)
        if (this.package_type === "document") {
          this.parcels[0].height = "41";
          this.parcels[0].width = "31";
          this.parcels[0].depth = "1";
          this.parcels.length = 1;

          if (!this.insureDocument) {
            delete this.requestBody.insured_value;
            delete this.requestBody.insured_value_currency;
          }
        } else {
          if (!this.insurePackage) {
            delete this.requestBody.insured_value;
            delete this.requestBody.insured_value_currency;
          }
        }

        this.requestBody.parcels = this.parcels;
        this.requestBody.package_type = this.package_type;

        this.setQuotingRequestBody(this.requestBody);
        this.$router.push({ name: "NewShipmentFlowFromHome" });
      } else {
        this.originIvalid = !validateOrigin;
        this.destinationInvalid = !validateDestination;
        this.isDocument = false;
        this.isBox = false;
        this.isPallet = false;
      }
    },
    validateAddress(input) {
      if (input.indexOf("-") >= 0) {
        let address = input.split("-");
        let length = address.length;
        if (length >= 3) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    checkAddressOrigin(event) {
      const query = event.target.value;
      if (query.length >= 3 && query.length <= 7) {
        this.getLocationInfo(query, this.origin.country_code).then(response => {
          this.originSuggestions = response.data.country_codes;
          this.results = response.data.country_codes;
          if (this.results.length > 0) {
            this.isOpen = true;
            this.isLoading = true;
            this.isBox = false;
            this.isPallet = false;
            this.isDocument = false;
          } else {
            this.isOpen = false;
            this.isLoading = false;
          }
        });
      }
    },
    checkAddressDestination(event) {
      const query = event.target.value;
      if (query.length >= 3 && query.length <= 7) {
        if (this.destination.country_code !== "MX") {
          if (query.length >= 5 && query.length <= 7) {
            service.shipment.getPostalCodes(this.destination.country_code, query).then((response)=>{
              this.destinationSuggestions = response.data
              this.resultsDes = response.data
              if (this.resultsDes.length > 0) {
                this.isOpenDes = true;
                this.isLoadingDes = true;
                this.isBox = false;
                this.isPallet = false;
                this.isDocument = false;
              } else {
                this.isOpenDes = false;
                this.isLoadingDes = false;
              }
            }).catch((error)=>{
              console.log(error)
            })
          } else {
             console.log("Codigo postal incorrecto")
          }
        } else {
          this.getLocationInfo(query, this.destination.country_code).then(
            response => {
              // console.log(response.data)
              this.destinationSuggestions = response.data.country_codes;
              this.resultsDes = response.data.country_codes;
              if (this.resultsDes.length > 0) {
                this.isOpenDes = true;
                this.isLoadingDes = true;
                this.isBox = false;
                this.isPallet = false;
                this.isDocument = false;
              } else {
                this.isOpenDes = false;
                this.isLoadingDes = false;
              }
            }
          );
        }
      }
    },
    getLocationInfo(query, countryCode) {
      return axios.get(
        `${
          commons.apiURL
        }/api/v1/postalcodes?search=${query}&country=${countryCode}`
      );
    },
    setOrigin(country) {
      this.origin = country;
    },
    setDestination(country) {
      this.destination = country;
    },
    addParcel() {
      this.parcels.push({
        depth: "",
        width: "",
        height: "",
        weight: ""
      });
    },
    removeParcel(index) {
      if (this.parcels.length > 1) {
        this.parcels.splice(index, 1);
        console.log(this.parcels);
      }
    },
    setResult(result) {
      this.origin.input =
        result.postal_code + " - " + result.place_name + " - " + result.city;
      this.isOpen = false;
    },
    onArrowDown() {
      if (this.arrowCounter <= this.results.length - 1 && this.isOpen) {
        if (this.arrowCounter === this.results.length - 1) {
          this.scroll = 0;
          this.arrowCounter = 0;
          this.origin.input =
            this.results[this.arrowCounter].postal_code +
            " - " +
            this.results[this.arrowCounter].place_name +
            " - " +
            this.results[this.arrowCounter].city;
          this.scrollTo(this.scroll);
        } else {
          if (this.arrowCounter < 0) {
            this.arrowCounter = this.arrowCounter + 1;
            this.origin.input =
              this.results[this.arrowCounter].postal_code +
              " - " +
              this.results[this.arrowCounter].place_name +
              " - " +
              this.results[this.arrowCounter].city;
            this.scroll = 0;
            this.scrollTo(this.scroll);
          } else {
            this.arrowCounter = this.arrowCounter + 1;
            this.origin.input =
              this.results[this.arrowCounter].postal_code +
              " - " +
              this.results[this.arrowCounter].place_name +
              " - " +
              this.results[this.arrowCounter].city;
            this.scroll = this.scroll + 30;
            this.scrollTo(this.scroll);
          }
        }
      }

      if (this.arrowCounter > this.results.length - 1 && this.isOpen) {
        this.arrowCounter = 0;
      }
    },
    onArrowUp() {
      if (this.arrowCounter === 0) {
        this.origin.input =
          this.results[this.arrowCounter].postal_code +
          " - " +
          this.results[this.arrowCounter].place_name +
          " - " +
          this.results[this.arrowCounter].city;
        this.arrowCounter = this.arrowCounter - 1;
        this.scroll = this.scroll - 30;
        this.scrollTo(this.scroll);
      }

      if (this.arrowCounter > 0 && this.isOpen) {
        this.arrowCounter = this.arrowCounter - 1;
        this.origin.input =
          this.results[this.arrowCounter].postal_code +
          " - " +
          this.results[this.arrowCounter].place_name +
          " - " +
          this.results[this.arrowCounter].city;
        this.scroll = this.scroll - 30;
        this.scrollTo(this.scroll);
      }

      if (this.arrowCounter < 0 && this.isOpen) {
        this.arrowCounter = this.results.length - 1;
        this.origin.input =
          this.results[this.arrowCounter].postal_code +
          " - " +
          this.results[this.arrowCounter].place_name +
          " - " +
          this.results[this.arrowCounter].city;
        this.scroll = 30 * this.results.length - 1 - 30;
        this.scrollTo(this.scroll);
      }
    },
    onEnter() {
      this.origin.input =
        this.results[this.arrowCounter].postal_code +
        " - " +
        this.results[this.arrowCounter].place_name +
        " - " +
        this.results[this.arrowCounter].city;
      this.isOpen = false;
      this.arrowCounter = -1;
    },
    scrollTo(coord) {
      let element = document.getElementById("autocomplete-results");
      element.scrollTo(0, coord);
    },
    handleClickOutside(evt) {
      if (evt.target.id !== "resultOrigin") {
        this.isOpen = false;
        this.isLoading = false;
        this.arrowCounter = 0;
        this.scroll = 0;
      }
      if (evt.target.id !== "resultDestination") {
        this.isOpenDes = false;
        this.isLoadingDes = false;
        this.arrowCounterDes = 0;
        this.scrollDes = 0;
      }
      /*if (evt.target.id !== "isDocument") {
        console.log("isDocument",this.isDocument)
        this.isDocument = false;
      }

      if (evt.target.id !== "isBox" && this.isBox) {
        this.isBox = false;


      }

      if (evt.target.id !== "isPallet" && this.isPallet) {
        this.isPallet = false;
      }
      */
    },

    setResultDes(result) {
      this.destination.input =
        result.postal_code + " - " + result.place_name + " - " + result.city;
        this.requestBody.destination_direction.state_short = (result.state_short)?result.state_short:"";
      this.isOpenDes = false;
    },
    onArrowDownDes() {
      if (
        this.arrowCounterDes <= this.resultsDes.length - 1 &&
        this.isOpenDes
      ) {
        if (this.arrowCounterDes === this.resultsDes.length - 1) {
          this.scrollDes = 0;
          this.arrowCounterDes = 0;
          this.destination.input =
            this.resultsDes[this.arrowCounterDes].postal_code +
            " - " +
            this.resultsDes[this.arrowCounterDes].place_name +
            " - " +
            this.resultsDes[this.arrowCounterDes].city;
          this.scrollToDes(this.scrollDes);
        } else {
          if (this.arrowCounterDes < 0) {
            this.arrowCounterDes = this.arrowCounterDes + 1;
            this.destination.input =
              this.resultsDes[this.arrowCounterDes].postal_code +
              " - " +
              this.resultsDes[this.arrowCounterDes].place_name +
              " - " +
              this.resultsDes[this.arrowCounterDes].city;
            this.scrollDes = 0;
            this.scrollToDes(this.scrollDes);
          } else {
            this.arrowCounterDes = this.arrowCounterDes + 1;
            this.destination.input =
              this.resultsDes[this.arrowCounterDes].postal_code +
              " - " +
              this.resultsDes[this.arrowCounterDes].place_name +
              " - " +
              this.resultsDes[this.arrowCounterDes].city;
            this.scrollDes = this.scrollDes + 30;
            this.scrollToDes(this.scrollDes);
          }
        }
      }

      if (this.arrowCounterDes > this.resultsDes.length - 1 && this.isOpenDes) {
        this.arrowCounterDes = 0;
      }
    },
    onArrowUpDes() {
      if (this.arrowCounterDes === 0) {
        this.destination.input =
          this.resultsDes[this.arrowCounterDes].postal_code +
          " - " +
          this.resultsDes[this.arrowCounterDes].place_name +
          " - " +
          this.resultsDes[this.arrowCounterDes].city;
        this.arrowCounterDes = this.arrowCounterDes - 1;
        this.scrollDes = this.scrollDes - 30;
        this.scrollToDes(this.scrollDes);
      }
      if (this.arrowCounterDes > 0 && this.isOpenDes) {
        this.scrollDes = this.scrollDes - 30;
        this.arrowCounterDes = this.arrowCounterDes - 1;
        this.destination.input =
          this.resultsDes[this.arrowCounterDes].postal_code +
          " - " +
          this.resultsDes[this.arrowCounterDes].place_name +
          " - " +
          this.resultsDes[this.arrowCounterDes].city;
        this.scrollToDes(this.scrollDes);
      }

      if (this.arrowCounterDes < 0 && this.isOpenDes) {
        this.arrowCounterDes = this.resultsDes.length - 1;
        this.destination.input =
          this.resultsDes[this.arrowCounterDes].postal_code +
          " - " +
          this.resultsDes[this.arrowCounterDes].place_name +
          " - " +
          this.resultsDes[this.arrowCounterDes].city;
        this.scrollDes = 30 * this.resultsDes.length - 1 - 30;
        this.scrollToDes(this.scrollDes);
      }
    },
    onEnterDes() {
      this.destination.input =
        this.resultsDes[this.arrowCounterDes].postal_code +
        " - " +
        this.resultsDes[this.arrowCounterDes].place_name +
        " - " +
        this.resultsDes[this.arrowCounterDes].city;
      this.isOpenDes = false;
      this.arrowCounterDes = -1;
    },
    scrollToDes(coord) {
      let element = document.getElementById("autocomplete-results-des");
      element.scrollTo(0, coord);
    },
    openFromDomcument() {
      this.package_type = "document";
      this.isDocument = true;
      this.isBox = false;
      this.isPallet = false;
    },
    openFromBox() {
      this.package_type = "box";
      this.isDocument = false;
      this.isBox = true;
      this.isPallet = false;
    },
    openFromPallet() {
      this.package_type = "tarima";
      this.isDocument = false;
      this.isBox = false;
      this.isPallet = true;
      console.log(this.isDocument, this.isBox, this.isPallet);
    },
    closeQuoting() {
      this.isDocument = false;
      this.isBox = false;
      this.isPallet = false;
    },
    addEventPixeleFacebook() {
      fbq("track", "ViewContent");
    },
    setDocument() {
      this.package_type = "document";
      console.log(this.package_type);
    },
    setBox() {
      this.package_type = "box";
      console.log(this.package_type);
    },
    /* "tarima" (no "pallet") es el valor que reconoce el backend:
       serviceEpak.js compara `params.package_type == "tarima"` para pedir
       tarifa de tarima. Con cualquier otro valor cotiza como paquete. */
    setPallet() {
      this.package_type = "tarima";
      console.log(this.package_type);
    },
    onSlideStart(slide) {
      this.sliding = true;
    },
    onSlideEnd(slide) {
      this.sliding = false;
    },
    ...mapMutations(["setQuotingRequestBody", "setCountryDes"]),
  },
  watch:{
    "destination.country_code":function (newVal, oldVal) {
      this.setCountryDes(newVal)
    }
  },
  created() {

    /** Verificar que tipo de envio se cotizara  */
    console.log("this.ecommerceShipment: ", this.ecommerceShipment);
    if (this.ecommerceShipment) {
      this.destination.input = this.ecommerceShipment.destination_direction
        .postal_code
        ? this.ecommerceShipment.destination_direction.postal_code
        : "";
    }

    /** Verificar que tipo de envio se cotizara  */

    Tawk_API.onLoad = function() {
      Tawk_API.showWidget();
      Tawk_API.minimize();
    };
    const wow = new WOW();
    wow.init();
    axios.get(`${commons.apiURL}/api/v1/countrycodes`).then(response => {
      this.countries = response.data.country_codes;
      this.quotingLocked = false;
    });
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this.setupReveal();
    try {
      Tawk_API.showWidget();
      Tawk_API.minimize();
    } catch (error) {}
  },
  destroyed() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    if (this.revealObserver) this.revealObserver.disconnect();
    if (this.demoObserver) this.demoObserver.disconnect();
    this.stopDemo();
    Tawk_API.hideWidget();
  },
  computed: {
    ...mapGetters(["ecommerceShipment"]),
    demoUrl() {
      return [
        "app.moovet.com.mx/cotizar",
        "app.moovet.com.mx/paqueterias",
        "app.moovet.com.mx/envios"
      ][this.demoStep - 1];
    }
  },
  components: {
    navbar: Navbar,
    "navbar-public": NavbarPublic,
    "footer-public": FooterPublic,
    "ph-icon": PhIcon
  }
};
</script>

<style scoped lang="scss">
@import "../assets/styles/app-tokens";
@import "../assets/styles/ew-components";

.ew-landing {
  @include app-tokens;
  background: var(--app-canvas);
  font-family: var(--app-font);
  color: var(--app-navy-secondary);
}

/* Contenedores y tipografía base ------------------------------------------ */
.ew-container {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 1.5rem;

  &--narrow { max-width: 780px; }
}

.ew-h2 {
  font-size: clamp(1.45rem, 3vw, 2.05rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--app-navy);
  margin: 0 0 1.75rem;
}

.ew-lead {
  font-size: 1.02rem;
  line-height: 1.6;
  max-width: 54ch;
  margin-bottom: 1.5rem;
}

.ew-section-block {
  padding: clamp(3rem, 7vw, 4.75rem) 0;

  &--muted { background: var(--app-surface-muted); }
}

/* Botones ------------------------------------------------------------------ */
.ew-btn-primary {
  @include ew-btn-primary;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 3rem;
  padding: 0 1.75rem;
  text-decoration: none;
}

.ew-btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 3rem;
  padding: 0 1.75rem;
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-radius: 999px;
  background: none;
  color: #fff;
  font-family: var(--app-font);
  font-size: 0.92rem;
  font-weight: 700;

  &:hover { background: rgba(255, 255, 255, 0.12); }
  &:focus-visible { outline: 3px solid #fff; outline-offset: 3px; }
}

.ew-link-btn {
  display: inline-block;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--app-accent-button);

  &:hover { text-decoration: underline; }
}

/* Hero --------------------------------------------------------------------- */
.ew-hero {
  padding: clamp(2rem, 5vw, 3.75rem) 0 clamp(2.5rem, 5vw, 3.5rem);
}

.ew-hero__inner {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.25rem;
  align-items: start;
}

.ew-hero__title {
  font-size: clamp(2rem, 4.6vw, 3.1rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.025em;
  color: var(--app-navy);
  margin: 0 0 1.1rem;
}

.ew-hero__subtitle {
  font-size: clamp(1rem, 1.6vw, 1.12rem);
  line-height: 1.6;
  max-width: 52ch;
  margin: 0 0 1.75rem;
}

.ew-hero__points {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--app-navy);
  }

  svg {
    width: 1.15rem;
    height: 1.15rem;
    flex-shrink: 0;
    color: var(--app-accent);
  }
}

/* Visual del hero: una sola secuencia al cargar (paquete viaja de origen a
   destino, los precios aparecen al "llegar"), no un loop continuo — el
   único movimiento ambiental de la página es el carrusel de paqueterías. */
.ew-hero__compare {
  margin-top: 2.25rem;
  padding-top: 1.75rem;
  border-top: 1px solid var(--app-border);
}

.ew-hero__route {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
}

.ew-hero__pin {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;

  b {
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--app-navy-secondary);
    white-space: nowrap;
  }
}

.ew-hero__pin-icon {
  width: 2.15rem;
  height: 2.15rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--app-navy);
  color: #fff;

  svg { width: 1.05rem; height: 1.05rem; }
}

.ew-hero__route-line {
  position: relative;
  height: 2px;
  background-image: repeating-linear-gradient(
    90deg, var(--app-border) 0 6px, transparent 6px 13px
  );
}

.ew-hero__route-package {
  position: absolute;
  top: 50%;
  left: 0;
  width: 1.6rem;
  height: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--app-accent);
  color: #fff;
  transform: translateY(-50%);
  animation: ew-route-travel 3s cubic-bezier(0.4, 0, 0.2, 1) 0.5s 1 both;

  svg { width: 0.85rem; height: 0.85rem; }
}

@keyframes ew-route-travel {
  0% { left: 0; opacity: 1; }
  82% { left: calc(100% - 1.6rem); opacity: 1; }
  100% { left: calc(100% - 1.6rem); opacity: 0; }
}

.ew-hero__quotes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin: 1.1rem 0 0;
}

.ew-hero__quote {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--app-border);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--app-navy-secondary);
  opacity: 0;
  animation: ew-quote-in 0.5s ease-out forwards;

  b { color: var(--app-navy); font-weight: 800; }

  &--best {
    border-color: var(--app-accent);
    b { color: var(--app-accent); }
  }

  &:nth-child(1) { animation-delay: 2.55s; }
  &:nth-child(2) { animation-delay: 2.7s; }
  &:nth-child(3) { animation-delay: 2.85s; }
}

@keyframes ew-quote-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.ew-hero__compare-caption {
  margin: 0.75rem 0 0;
  font-size: 0.72rem;
  color: var(--app-muted);
}

/* Widget de cotización ------------------------------------------------------ */
.ew-quote {
  background: #fff;
  border: 1px solid var(--app-border);
  border-radius: 1rem;
  box-shadow: 0 18px 40px rgba(7, 50, 88, 0.10);
  padding: 1.5rem;
}

.ew-quote__title {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--app-navy);
  margin: 0 0 1.25rem;
}

.ew-quote__row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;

  > .ew-quote__field { flex: 1 1 190px; min-width: 0; }
}

.ew-quote__field { position: relative; }

.ew-quote__label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--app-navy);
  margin-bottom: 0.35rem;

  &--block { margin-top: 0.25rem; }
}

.ew-quote__input-group {
  display: flex;
  align-items: stretch;
  border: 1.5px solid var(--app-border);
  border-radius: var(--app-radius-sm);
  background: #fff;
  overflow: hidden;

  &:focus-within { border-color: var(--app-accent); }
}

.ew-quote__country {
  display: flex;
  align-items: center;
  padding: 0 0.7rem;
  background: var(--app-surface-muted);
  border-right: 1px solid var(--app-border);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--app-navy-secondary);
  white-space: nowrap;
}

.ew-quote__country-dd {
  /deep/ .btn {
    height: 100%;
    padding: 0 0.7rem;
    background: var(--app-surface-muted);
    border: none;
    border-right: 1px solid var(--app-border);
    border-radius: 0;
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--app-navy-secondary);
    text-decoration: none;
  }
  /deep/ .dropdown-menu {
    max-height: 260px;
    overflow-y: auto;
    border: 1px solid var(--app-border);
    border-radius: var(--app-radius-sm);
  }
  /deep/ .dropdown-item {
    font-size: 0.85rem;
    color: var(--app-navy-secondary);
  }
}

.ew-quote__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 2.75rem;
  padding: 0 0.85rem;
  border: none;
  background: transparent;
  font-family: var(--app-font);
  font-size: 0.92rem;
  color: var(--app-navy);

  &::placeholder { color: var(--app-muted); }
  &:focus { outline: none; }

  &--solo {
    width: 100%;
    border: 1.5px solid var(--app-border);
    border-radius: var(--app-radius-sm);

    &:focus { border-color: var(--app-accent); }
  }
}

.ew-quote__suggestions-wrap { position: relative; }

.ew-quote__error {
  margin: 0.4rem 0 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #C8102E;
}

.ew-quote__types {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
}

.ew-pill {
  display: inline-flex;
  align-items: center;
  height: 2.5rem;
  padding: 0 1.2rem;
  margin: 0;
  border: 1.5px solid var(--app-border);
  border-radius: 999px;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--app-navy);
  cursor: pointer;

  input { position: absolute; opacity: 0; width: 0; height: 0; }
  &:hover { background: var(--app-surface-muted); }
  &.is-active { border-color: var(--app-accent); background: var(--app-surface-muted); }
}

.ew-quote__parcels {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.1rem;

  > .ew-quote__field { flex: 1 1 96px; min-width: 0; }
}

.ew-quote__insurance {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1.25rem;
  font-size: 0.88rem;
  color: var(--app-navy-secondary);
  cursor: pointer;

  input { @include ew-checkbox; }
}

.ew-quote__submit { width: 100%; }

.ew-quote__hint {
  margin: 0.9rem 0 0;
  font-size: 0.78rem;
  line-height: 1.5;
  color: var(--app-muted);
}

/* Paqueterías -------------------------------------------------------------- */
/* Es el único movimiento continuo de la página: la tira se desplaza sola
   porque ya no cabe completa en una línea, y se detiene al pasar el cursor
   para poder leerla. */
.ew-carriers {
  --ew-carriers-gap: clamp(2rem, 6vw, 3.5rem);

  background: #fff;
  border-top: 1px solid var(--app-border);
  border-bottom: 1px solid var(--app-border);
  padding: 1.75rem 0;
  text-align: center;
}

/* El degradado de los bordes evita que los logos se corten en seco al
   entrar y salir. */
.ew-carriers__viewport {
  overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent);
}

.ew-carriers__track {
  display: flex;
  width: max-content;
  animation: ew-carriers-scroll 38s linear infinite;
}

.ew-carriers:hover .ew-carriers__track,
.ew-carriers:focus-within .ew-carriers__track {
  animation-play-state: paused;
}

/* El padding derecho vale lo mismo que el gap para que los dos grupos
   empalmen sin costura al reiniciar en -50%. */
.ew-carriers__group {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  list-style: none;
  margin: 0;
  padding: 0 var(--ew-carriers-gap) 0 0;
  gap: var(--ew-carriers-gap);

  li { display: flex; align-items: center; }

  /* A color: en gris el logo de DHL (rojo sobre amarillo sólido) queda como
     una plancha gris y pierde toda la fuerza de reconocimiento, que es justo
     para lo que está la tira. */
  img {
    display: block;
    height: 1.7rem;
    width: auto;
  }

  /* Cada marca tiene una proporción distinta; a la misma altura las muy
     alargadas pesan de más y las compactas de menos. */
  img[alt="FedEx"] { height: 2.2rem; }
  img[alt="DHL"] { height: 1.6rem; }
  img[alt="Estafeta"] { height: 1.4rem; }
  img[alt="ampm"] { height: 1.9rem; }
  img[alt="Tresguerras"] { height: 1.15rem; }
}

@keyframes ew-carriers-scroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

/* Cómo funciona ------------------------------------------------------------ */
.ew-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

.ew-step {
  background: #fff;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  padding: 1.5rem;
}

.ew-step__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin-bottom: 0.85rem;
  border-radius: 50%;
  background: var(--app-navy);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 800;
}

.ew-step__title {
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--app-navy);
  margin: 0 0 0.5rem;
}

.ew-step__text {
  font-size: 0.93rem;
  line-height: 1.6;
  margin: 0;
}

/* Banda de datos ----------------------------------------------------------- */
.ew-band {
  background: var(--app-navy);
  padding: clamp(2.25rem, 5vw, 3.25rem) 0;
  color: #fff;
}

/* Una columna en celular: con 3 cifras por banda, 2 columnas deja la
   tercera huérfana en su propia fila. */
.ew-band__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.75rem;
  text-align: center;
}

.ew-band__value {
  font-size: clamp(2rem, 4vw, 2.6rem);
  font-weight: 800;
  line-height: 1;
  margin: 0 0 0.45rem;
  color: #fff;
}

.ew-band__label {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.82);
  max-width: 22ch;
  margin-inline: auto;
}

/* Las cifras entran escalonadas junto con el conteo que dispara el
   IntersectionObserver. El estado inicial solo se aplica mientras la
   banda no se ha revelado, para que sin JS nada quede invisible. */
.ew-band.is-revealed .ew-band__item {
  animation: ew-stat-in 0.55s ease-out both;

  &:nth-child(2) { animation-delay: 0.12s; }
  &:nth-child(3) { animation-delay: 0.24s; }
}

@keyframes ew-stat-in {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Beneficios --------------------------------------------------------------- */
.ew-benefits {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

.ew-benefit {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.4rem 0;
  border-bottom: 1px solid var(--app-border);

  &:first-child { padding-top: 0; }
  &:last-child { border-bottom: none; }
}

.ew-benefit__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  flex-shrink: 0;
  border-radius: var(--app-radius-sm);
  background: var(--app-surface-muted);
  color: var(--app-accent);

  svg { width: 1.35rem; height: 1.35rem; }
}

.ew-benefit__title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--app-navy);
  margin: 0 0 0.3rem;
}

.ew-benefit__text {
  font-size: 0.93rem;
  line-height: 1.6;
  margin: 0;
  max-width: 62ch;
}

/* Vitrina del panel: el mockup de navegador va después del texto en el
   documento (es evidencia de apoyo, no la explicación en sí), pero se
   coloca a la izquierda en escritorio para alternar con el patrón
   texto-izquierda/visual-derecha del hero. */
.ew-showcase {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
}

.ew-showcase__visual { grid-row: 1; }
.ew-showcase__text { grid-row: 2; }

/* Con columnas explícitas distintas, dejar la fila en "auto" no basta:
   el cursor de auto-colocación de CSS Grid no reinicia en la fila 1 para
   el segundo elemento y quedan en filas separadas. Hay que fijar la
   misma fila (1) para los dos. */
@media (min-width: 992px) {
  .ew-showcase__visual { grid-row: 1; grid-column: 1; }
  .ew-showcase__text { grid-row: 1; grid-column: 2; }
}

.ew-browser-frame {
  background: #fff;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  box-shadow: 0 18px 40px rgba(7, 50, 88, 0.10);
  overflow: hidden;
}

.ew-browser-frame__bar {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 0.9rem;
  background: var(--app-surface-muted);
  border-bottom: 1px solid var(--app-border);
}

.ew-browser-frame__dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: var(--app-border);
}

.ew-browser-frame__url {
  margin-left: 0.6rem;
  padding: 0.2rem 0.7rem;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--app-border);
  font-size: 0.72rem;
  color: var(--app-muted);
}

/* Contenedor de proporción fija (1600×1025 de la captura) para que los tres
   pasos se releven sin que la página salte de alto al cambiar. */
.ew-demo {
  position: relative;
  width: 100%;
  padding-top: 64.06%;
  background: var(--app-canvas);
  overflow: hidden;
}

.ew-demo__pane {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 6%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5%;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.45s ease, transform 0.45s ease;
  pointer-events: none;
}

.ew-demo.is-step-1 .ew-demo__pane--form,
.ew-demo.is-step-2 .ew-demo__pane--rates,
.ew-demo.is-step-3 .ew-demo__pane--done {
  opacity: 1;
  transform: translateY(0);
}

.ew-demo__title {
  margin: 0;
  font-size: clamp(0.85rem, 1.7vw, 1.15rem);
  font-weight: 800;
  color: var(--app-navy);
}

.ew-demo__fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3%;
}

.ew-demo__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.ew-demo__label {
  font-size: clamp(0.55rem, 1vw, 0.7rem);
  font-weight: 700;
  color: var(--app-navy-secondary);
}

.ew-demo__input {
  padding: 0.65rem 0.75rem;
  background: #fff;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-sm);
  font-size: clamp(0.6rem, 1.15vw, 0.85rem);
  color: var(--app-navy);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Los campos se van llenando uno tras otro cada vez que el paso 1 entra. */
.ew-demo.is-step-1 .ew-demo__field {
  animation: ew-demo-field-in 0.4s ease-out both;

  &:nth-child(2) { animation-delay: 0.12s; }
  &:nth-child(3) { animation-delay: 0.24s; }
  &:nth-child(4) { animation-delay: 0.36s; }
}

@keyframes ew-demo-field-in {
  from { opacity: 0; transform: translateX(-6px); }
  to { opacity: 1; transform: translateX(0); }
}

.ew-demo__btn {
  align-self: stretch;
  padding: 0.7rem;
  border-radius: var(--app-radius-sm);
  background: var(--app-accent-button);
  color: #fff;
  text-align: center;
  font-size: clamp(0.55rem, 1.05vw, 0.78rem);
  font-weight: 700;
}

.ew-demo.is-step-1 .ew-demo__btn {
  animation: ew-demo-field-in 0.4s ease-out 0.48s both;
}

.ew-demo__rates {
  display: flex;
  flex-direction: column;
  gap: 3%;
}

.ew-demo__rate {
  display: grid;
  grid-template-columns: 22% 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 0.8rem;
  background: #fff;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-sm);

  img { max-width: 100%; max-height: 1.25rem; width: auto; object-fit: contain; }

  &.is-best {
    border-color: var(--app-accent);
    grid-template-columns: 22% 1fr auto auto;
  }
}

.ew-demo__rate-days {
  font-size: clamp(0.5rem, 0.95vw, 0.7rem);
  color: var(--app-muted);
}

.ew-demo__rate-price {
  font-size: clamp(0.6rem, 1.15vw, 0.85rem);
  font-weight: 800;
  color: var(--app-navy);
}

.ew-demo__rate.is-best .ew-demo__rate-price { color: var(--app-accent); }

.ew-demo__rate-tag {
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: var(--app-accent);
  color: #fff;
  font-size: clamp(0.42rem, 0.8vw, 0.6rem);
  font-weight: 700;
  white-space: nowrap;
}

/* Las opciones aparecen una por una, como cuando llegan las cotizaciones. */
.ew-demo.is-step-2 .ew-demo__rate {
  animation: ew-demo-field-in 0.4s ease-out both;

  &:nth-child(2) { animation-delay: 0.14s; }
  &:nth-child(3) { animation-delay: 0.28s; }
}

.ew-demo__pane--done {
  padding: 0;
  gap: 0;
}

.ew-demo__shot {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top left;
}

.ew-demo__toast {
  position: absolute;
  right: 5%;
  bottom: 7%;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: var(--app-navy);
  color: #fff;
  font-size: clamp(0.55rem, 1vw, 0.75rem);
  font-weight: 700;
  box-shadow: 0 8px 20px rgba(7, 50, 88, 0.25);

  svg { width: 0.9rem; height: 0.9rem; }
}

.ew-demo.is-step-3 .ew-demo__toast {
  animation: ew-demo-toast-in 0.45s ease-out 0.5s both;
}

@keyframes ew-demo-toast-in {
  from { opacity: 0; transform: translateY(8px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* Indicador de pasos: hace de leyenda de lo que se está viendo. */
.ew-demo-steps {
  list-style: none;
  display: flex;
  gap: 0.4rem;
  margin: 1rem 0 0;
  padding: 0;

  li {
    flex: 1;
    padding-top: 0.6rem;
    border-top: 2px solid var(--app-border);
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--app-muted);
    transition: color 0.3s ease, border-color 0.3s ease;

    &.is-done { border-top-color: var(--app-navy-secondary); color: var(--app-navy-secondary); }
    &.is-active { border-top-color: var(--app-accent); color: var(--app-navy); }
  }
}

/* Integraciones ------------------------------------------------------------ */
.ew-integrations {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
}

.ew-channels {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

.ew-channel {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1rem 1.1rem;
  background: #fff;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  transition: transform 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: var(--app-navy-secondary);
  }

  /* La de IA todavia no existe, asi que va en tono apagado y sin el
     realce del hover: no se promete como algo que ya se pueda usar. */
  &--soon {
    background: transparent;
    border-style: dashed;

    &:hover { transform: none; border-color: var(--app-border); }
  }
}

/* Mas ancha que alta porque los logos de las tiendas son apaisados (el de
   WooCommerce es casi 4:1 y en una casilla cuadrada quedaba minusculo). */
.ew-channel__mark {
  width: 3.25rem;
  height: 2.6rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--app-radius-sm);
  background: var(--app-surface-muted);

  img { max-width: 2.6rem; max-height: 1.6rem; width: auto; object-fit: contain; }

  &--icon {
    width: 2.6rem;
    background: var(--app-navy);
    color: #fff;

    svg { width: 1.25rem; height: 1.25rem; }
  }
}

.ew-channel--soon .ew-channel__mark--icon {
  background: #fff;
  border: 1px solid var(--app-border);
  color: var(--app-muted);
}

.ew-channel__name {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin: 0 0 0.2rem;
  font-size: 0.92rem;
  font-weight: 800;
  color: var(--app-navy);
}

.ew-channel__badge {
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: var(--app-surface-muted);
  border: 1px solid var(--app-border);
  font-size: 0.66rem;
  font-weight: 700;
  color: var(--app-muted);
}

.ew-channel__text {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--app-muted);
}

/* Entran escalonadas cuando la seccion cruza el umbral del observer. */
.ew-integrations.is-revealed .ew-channel {
  animation: ew-channel-in 0.5s ease-out both;

  &:nth-child(2) { animation-delay: 0.1s; }
  &:nth-child(3) { animation-delay: 0.2s; }
  &:nth-child(4) { animation-delay: 0.3s; }
}

@keyframes ew-channel-in {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (min-width: 560px) {
  .ew-channels { grid-template-columns: repeat(2, 1fr); gap: 0.9rem; }
}

/* FAQ ---------------------------------------------------------------------- */
.ew-faq {
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  background: #fff;
}

.ew-faq__item {
  padding: 1.25rem 1.4rem;
  border-bottom: 1px solid var(--app-border);

  &:last-child { border-bottom: none; }
}

.ew-faq__q {
  font-size: 0.97rem;
  font-weight: 800;
  color: var(--app-navy);
  margin: 0 0 0.4rem;
}

.ew-faq__a {
  font-size: 0.92rem;
  line-height: 1.6;
  margin: 0;
}

/* Cierre ------------------------------------------------------------------- */
.ew-final {
  background: var(--app-navy);
  padding: clamp(3rem, 6vw, 4.5rem) 0;
}

.ew-final__inner { text-align: center; }

.ew-final__title {
  font-size: clamp(1.5rem, 3.2vw, 2.1rem);
  font-weight: 800;
  color: #fff;
  margin: 0 0 0.6rem;
}

.ew-final__text {
  color: rgba(255, 255, 255, 0.82);
  margin: 0 0 1.75rem;
}

.ew-final__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
}

/* Responsivo --------------------------------------------------------------- */
@media (min-width: 700px) {
  .ew-steps { grid-template-columns: repeat(3, 1fr); }
  .ew-band__grid { grid-template-columns: repeat(3, 1fr); gap: 2rem 1.5rem; }
}

@media (min-width: 992px) {
  .ew-hero__inner { grid-template-columns: 1fr 1fr; gap: 3rem; align-items: center; }
  .ew-quote { padding: 1.75rem; }
  .ew-showcase { grid-template-columns: 1fr 1fr; gap: 3.5rem; align-items: center; }
  .ew-integrations { grid-template-columns: 1fr 1.15fr; gap: 3.5rem; }
}

@media (prefers-reduced-motion: reduce) {
  /* Sin movimiento la tira se queda quieta y acomodada en varias líneas;
     el grupo duplicado solo existe para el bucle, así que sobra. */
  .ew-carriers__track {
    animation: none;
    width: 100%;
    justify-content: center;
  }
  .ew-carriers__group {
    flex-wrap: wrap;
    justify-content: center;
    padding: 0 1.5rem;
    row-gap: 1.5rem;

    &[aria-hidden="true"] { display: none; }
  }

  /* El paquete se queda a medio camino y los precios ocultos: se salta
     directo al estado final, con todo visible. */
  .ew-hero__route-package { animation: none; left: calc(100% - 1.6rem); opacity: 0; }
  .ew-hero__quote { animation: none; opacity: 1; transform: none; }

  /* Todo lo que entra por scroll o por la simulación se muestra fijo. */
  .ew-band__item,
  .ew-channel,
  .ew-demo__field,
  .ew-demo__btn,
  .ew-demo__rate,
  .ew-demo__toast {
    animation: none !important;
    opacity: 1;
    transform: none;
  }
  .ew-demo__pane { transition: none; }
}
</style>

<!-- Estas reglas quedan SIN scope a propósito: el autocompletado de código
     postal (.autocomplete-results/.autocomplete-result) lo comparten
     Addresses.vue y NewShipmentFlowFromDashboard.vue, y .ew-custom-dropdown
     lo usan los dos navbars. Antes vivían en el bloque global de este
     archivo; si se scopan, esas pantallas se quedan sin estilo. -->
<style lang="scss">
@import "../assets/styles/variables";

.autocomplete-results {
  position: absolute;
  background-color: #fff;
  padding: 0.3rem;
  margin: 0;
  overflow: auto;
  max-height: 200px;
  z-index: 9999999;
  width: 100%;
  border: 1px solid #D9E2E8;
  border-radius: 0.5rem;
  box-shadow: 0 12px 28px rgba(7, 50, 88, 0.12);
}

.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 0.4rem 0.6rem;
  border-radius: 0.35rem;
  font-size: 0.85rem;
  background-color: #fff;
  cursor: pointer;
}

.autocomplete-result.is-active,
.autocomplete-result:hover {
  background-color: $primary;
  color: white;
}

.txt-f1 { margin-top: 10px; }

.ew-custom-dropdown {
  input { width: 100% !important; }
  .btn { border: 1px solid #ced4da; }
  .btn-link {
    text-decoration: none;
    color: #333;
    border: 1px solid transparent;
  }
  .ew-dropdown--fixed-height {
    max-height: 400px;
    overflow-y: auto;
    .dropdown-item img {
      max-width: 2rem;
      margin-right: 1rem;
    }
  }
  .ew-dropdown-checkbox { min-width: 12px; }
  .ew-flag { max-width: 1.8rem; }
  .ew-icon { max-width: 1.3rem; max-height: 1.3rem; }
  .ew-dropdown-caret { max-height: 1rem; }

  &.no-radius-right .btn {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Home.vue